// Deal closings (202609301300): what a Won deal closed at.
//
// Three pieces, all on window.* for the deal page and the deals grid:
//   ClosingDrawer    the form — opened by a move to Won (stage rail, grid
//                    stage cell) and by Edit on a recorded closing. It only
//                    collects; the caller decides whether to PUT the closing
//                    alone or send it with the stage PATCH (`closing`).
//   ClosingSection   the deal page block for a Won deal: price, multiples,
//                    buyer, fee — or "not recorded" with the button.
//   ClosedDealsStrip the Won tab's header: median multiples and fees by
//                    industry and year from /closings/stats.
// Multiples are never typed: the server generates EV / revenue and EV / EBITDA.

const WON_STAGE_SLUGS = new Set(['won', 'ss_won']);
const isWonSlug = (slug) => WON_STAGE_SLUGS.has(slug);

const BUYER_TYPE_OPTS = [
  { value: 'pe_platform',   label: 'PE — new platform' },
  { value: 'pe_addon',      label: 'PE — add-on to a portfolio company' },
  { value: 'strategic',     label: 'Strategic (operating company)' },
  { value: 'family_office', label: 'Family office' },
  { value: 'search_fund',   label: 'Search fund' },
  { value: 'individual',    label: 'Individual' },
];
const BUYER_TYPE_LABEL = Object.fromEntries(BUYER_TYPE_OPTS.map((o) => [o.value, o.label]));
const KIND_OPTS = [
  { value: 'success',  label: 'Success fee' },
  { value: 'retainer', label: 'Retainer + success' },
  { value: 'sellside', label: 'Full sell-side' },
];

const fmtX = (x) => (x == null || !Number.isFinite(Number(x)) ? '—' : `${Number(x).toFixed(1)}×`);
const money = (v) => (v == null ? '—' : window.fmtMoney(v));
const NEW_BUYER = '__typed:';

// Preview multiples while typing, same arithmetic as the generated columns.
const previewMultiple = (ev, base) => (ev > 0 && base > 0 ? ev / base : null);

const ClosingDrawer = ({ open, deal, closing, onClose, onSubmit, title, zIndex }) => {
  // Phone: the Drawer is a full-screen sheet; the paired fields stack and the
  // footer puts the hint above full-width buttons. Desktop layout unchanged.
  const phone = !!window.useDevice().phone;
  const pair = phone ? '1fr' : '1fr 1fr';
  const [f, setF] = React.useState({});
  const [buyerPick, setBuyerPick] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    if (!open) return;
    const c = closing || {};
    setF({
      closed_on: c.closed_on || window.localISODate(),
      enterprise_value: c.enterprise_value ?? null,
      fee_usd: c.fee_usd ?? deal?.size ?? null,
      seller_revenue: c.seller_revenue ?? deal?.reportedRevenue ?? deal?.companyRevenueEst ?? null,
      seller_ebitda: c.seller_ebitda ?? deal?.reportedEbitda ?? deal?.companyEbitdaEst ?? null,
      buyer_company_id: c.buyer_company_id || null,
      buyer_name: c.buyer_name || '',
      buyer_type: c.buyer_type || null,
      structure: c.structure || '',
      earnout_usd: c.earnout_usd ?? null,
      engagement_kind: c.engagement_kind || 'success',
      notes: c.notes || '',
    });
    setBuyerPick(c.buyer_company_id ? { value: c.buyer_company_id, label: c.buyer_name || 'Buyer' } : (c.buyer_name ? { value: NEW_BUYER + c.buyer_name, label: c.buyer_name } : null));
    setBusy(false); setErr(null);
  }, [open, closing, deal]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  // Combobox reports { target: { value }, value, label }; MoneyInput reports the number.
  const pick = (k) => (ev) => set(k)(ev && typeof ev === 'object' ? (ev.value ?? ev.target?.value ?? null) : ev);
  const evX = previewMultiple(Number(f.enterprise_value), Number(f.seller_ebitda));
  const revX = previewMultiple(Number(f.enterprise_value), Number(f.seller_revenue));
  const ready = Number(f.enterprise_value) > 0 && !!f.closed_on && !!(f.buyer_name || '').trim();

  const loadBuyers = React.useCallback(async (q) => {
    const opts = window.searchCompanyOptions ? await window.searchCompanyOptions(q) : [];
    const typed = (q || '').trim();
    return typed && !opts.some((o) => (o.label || '').toLowerCase() === typed.toLowerCase())
      ? [...opts, { value: NEW_BUYER + typed, label: `“${typed}” — not in Atlas, keep as typed` }]
      : opts;
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = { ...f };
      for (const k of ['enterprise_value', 'fee_usd', 'seller_revenue', 'seller_ebitda', 'earnout_usd']) {
        payload[k] = payload[k] === '' || payload[k] == null ? null : Number(payload[k]);
      }
      payload.buyer_name = (payload.buyer_name || '').trim() || null;
      await onSubmit(payload);
    } catch (e) {
      setErr(e?.message || 'Could not save');
      setBusy(false);
    }
  };

  const Field = window.Field; const MoneyInput = window.MoneyInput; const TextInput = window.TextInput;
  return (
    <window.Drawer
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      width={520}
      title={title || (closing ? 'Edit closing' : 'Close the deal')}
      subtitle={deal ? (deal.targetName || deal.name) : undefined}
      footer={phone ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(err || !ready) && (
            <span style={{ fontSize: 13, color: err ? 'var(--rose-fg, oklch(0.50 0.14 25))' : 'var(--text-3)' }}>
              {err || 'Price, buyer and close date are required.'}
            </span>
          )}
          <span style={{ display: 'flex', gap: 10 }}>
            <window.Btn variant="outline" onClick={onClose} disabled={busy} style={{ flex: 1, justifyContent: 'center' }}>Cancel</window.Btn>
            <window.Btn variant="primary" disabled={!ready || busy} onClick={submit} style={{ flex: 1, justifyContent: 'center' }}>
              {busy ? 'Saving…' : (closing ? 'Save' : 'Mark Won')}
            </window.Btn>
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: err ? 'var(--rose-fg, oklch(0.50 0.14 25))' : 'var(--text-4)' }}>
            {err || (ready ? '' : 'Price, buyer and close date are required.')}
          </span>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <window.Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</window.Btn>
            <window.Btn size="sm" variant="primary" disabled={!ready || busy} onClick={submit}>
              {busy ? 'Saving…' : (closing ? 'Save' : 'Mark Won')}
            </window.Btn>
          </span>
        </div>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: pair, columnGap: 14 }}>
        <Field label="Enterprise value (price paid)">
          <MoneyInput value={f.enterprise_value} onChange={set('enterprise_value')} placeholder="e.g. 12.5M" />
        </Field>
        <Field label="Closed on">
          <TextInput type="date" value={f.closed_on || ''} onChange={(e) => set('closed_on')(e.target.value)} />
        </Field>
        <Field label="Seller revenue (at close)">
          <MoneyInput value={f.seller_revenue} onChange={set('seller_revenue')} placeholder="e.g. 8M" />
        </Field>
        <Field label="Seller EBITDA (at close)">
          <MoneyInput value={f.seller_ebitda} onChange={set('seller_ebitda')} placeholder="e.g. 1.5M" />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 18, padding: '8px 12px', marginBottom: 12, background: 'var(--surface-2)', borderRadius: 8, fontSize: 12,
                    ...(phone ? { flexWrap: 'wrap', rowGap: 4, columnGap: 14, fontSize: 14 } : null) }}>
        <span><span style={{ color: 'var(--text-4)' }}>EBITDA multiple</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtX(evX)}</b></span>
        <span><span style={{ color: 'var(--text-4)' }}>Revenue multiple</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtX(revX)}</b></span>
      </div>
      <Field label="Buyer" hint="Pick the Atlas record when it exists — that is what links the closing to the buyer's acquisitions.">
        <window.Combobox
          value={buyerPick ? buyerPick.value : null}
          options={buyerPick ? [buyerPick] : []}
          loadOptions={loadBuyers}
          searchable
          placeholder="Search buyers…"
          onChange={(ev) => {
            const v = ev && typeof ev === 'object' ? (ev.value ?? ev.target?.value ?? null) : ev;
            if (v == null) { setBuyerPick(null); setF((p) => ({ ...p, buyer_company_id: null, buyer_name: '' })); return; }
            const typed = String(v).startsWith(NEW_BUYER);
            const label = typed ? String(v).slice(NEW_BUYER.length) : ((ev && ev.label) || (buyerPick && buyerPick.value === v ? buyerPick.label : ''));
            setBuyerPick({ value: v, label });
            setF((p) => ({ ...p, buyer_company_id: typed ? null : v, buyer_name: label }));
          }}
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: pair, columnGap: 14 }}>
        <Field label="Buyer type">
          <window.Combobox value={f.buyer_type} options={BUYER_TYPE_OPTS} onChange={pick('buyer_type')} placeholder="—" />
        </Field>
        <Field label="Engagement">
          <window.Combobox value={f.engagement_kind} options={KIND_OPTS} onChange={pick('engagement_kind')} />
        </Field>
        <Field label="Our fee at close">
          <MoneyInput value={f.fee_usd} onChange={set('fee_usd')} placeholder="e.g. 450k" />
        </Field>
        <Field label="Earnout (if any)">
          <MoneyInput value={f.earnout_usd} onChange={set('earnout_usd')} placeholder="e.g. 1M" />
        </Field>
      </div>
      <Field label="Structure" hint="Earnout terms, seller note, rollover %, cash at close — in words.">
        <TextInput value={f.structure} onChange={(e) => set('structure')(e.target.value)} placeholder="e.g. 80% cash, 20% rollover; $1M earnout over 2 years" />
      </Field>
      <Field label="Notes">
        <textarea value={f.notes} onChange={(e) => set('notes')(e.target.value)} rows={3}
          style={{ width: '100%', resize: 'vertical', padding: '8px 10px', fontSize: 12.5, background: 'var(--surface)',
                   color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'inherit' }} />
      </Field>
    </window.Drawer>
  );
};

// ── Deal page block ────────────────────────────────────────────────────────
const ClosingSection = ({ deal, sectionId = 'deal.closing' }) => {
  const [closing, setClosing] = React.useState(undefined);   // undefined = loading, null = none
  const [editing, setEditing] = React.useState(false);
  const won = deal && isWonSlug(deal.stage);
  // Only a Won deal, or one the engagement payload says has a closing on
  // file (has_closing), asks for it. hasClosing null = the payload didn't
  // say: ask.
  const wanted = !!won || (!!deal && deal.hasClosing !== false);
  const load = React.useCallback(() => {
    if (!deal?.id) return;
    if (!wanted) { setClosing(null); return; }
    window.AtlasApi.getClosing(deal.id).then(setClosing).catch(() => setClosing(null));
  }, [deal?.id, wanted]);
  React.useEffect(() => { load(); }, [load, deal?.stage]);
  if (!deal || (!won && !closing)) return null;
  const c = closing || null;
  const tile = (label, value, sub) => (
    <div style={{ minWidth: 120, flex: 1 }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{sub}</div>}
    </div>
  );
  return (
    <window.Section
      title="Closing"
      collapsible id={sectionId} defaultOpen
      preview={c ? `${money(c.enterprise_value)} · ${fmtX(c.ebitda_multiple)} EBITDA · ${c.buyer_name || '—'}` : 'not recorded'}
      action={window.AtlasIsGuest ? null : <window.Btn size="xs" variant="ghost" onClick={() => setEditing(true)}>{c ? 'Edit' : 'Record closing'}</window.Btn>}
    >
      {closing === undefined ? (
        <div style={{ padding: 14, fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>
      ) : !c ? null : (
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 10 }}>
            {tile('Enterprise value', money(c.enterprise_value), c.deal_size_est ? `est. ${money(c.deal_size_est)}` : null)}
            {tile('EBITDA multiple', fmtX(c.ebitda_multiple), c.seller_ebitda ? `on ${money(c.seller_ebitda)}` : null)}
            {tile('Revenue multiple', fmtX(c.revenue_multiple), c.seller_revenue ? `on ${money(c.seller_revenue)}` : null)}
            {tile('Our fee', money(c.fee_usd), c.followon_fee_usd ? `+ ${money(c.followon_fee_usd)} follow-on` : null)}
            {tile('Closed', c.closed_on ? window.fmtDate(c.closed_on) : '—', c.engagement_kind ? KIND_OPTS.find((k) => k.value === c.engagement_kind)?.label : null)}
          </div>
          <div style={{ fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-4)' }}>Buyer</span>
            {c.buyer_company_id ? (
              <button onClick={() => window.AtlasNav?.goToCompany?.(c.buyer_company_id)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: 12.5, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}>
                {c.buyer_name || 'Open buyer'}
              </button>
            ) : <b>{c.buyer_name || '—'}</b>}
            {c.buyer_type && <window.Pill hue="slate" sz="sm">{BUYER_TYPE_LABEL[c.buyer_type] || c.buyer_type}</window.Pill>}
            {c.earnout_usd ? <window.Pill hue="amber" sz="sm">earnout {money(c.earnout_usd)}</window.Pill> : null}
            {c.source === 'tracker_csv' && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>from the closed-deals tracker</span>}
          </div>
          {(c.structure || c.notes) && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.5 }}>
              {c.structure && <div>{c.structure}</div>}
              {c.notes && <div style={{ color: 'var(--text-4)' }}>{c.notes}</div>}
            </div>
          )}
        </div>
      )}
      <ClosingDrawer
        open={editing}
        deal={deal}
        closing={c}
        onClose={() => setEditing(false)}
        onSubmit={async (payload) => {
          const saved = await window.AtlasApi.saveClosing(deal.id, payload);
          setClosing(saved); setEditing(false);
          window.pushToast?.({ type: 'success', title: 'Closing saved' });
        }}
      />
    </window.Section>
  );
};

// ── Won tab strip ──────────────────────────────────────────────────────────
const ClosedDealsStrip = () => {
  const [stats, setStats] = React.useState(null);
  const [year, setYear] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.closingsStats(year ? { year } : {}).then((s) => { if (alive) setStats(s); }).catch(() => {});
    return () => { alive = false; };
  }, [year]);
  if (!stats || !stats.overall) return null;
  const o = stats.overall;
  const years = (stats.by_year || []).map((r) => r.key).filter(Boolean);
  const tile = (label, value, sub) => (
    <div style={{ padding: '8px 14px', borderRight: '1px solid var(--border)', minWidth: 130 }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{sub}</div>}
    </div>
  );
  const top = (stats.by_industry || []).filter((r) => r.n_mult >= 2).slice(0, 8);
  return (
    <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
        {tile('Closed deals', o.n.toLocaleString(), year ? `in ${year}` : 'all years')}
        {tile('Median EBITDA multiple', fmtX(o.med_ebitda_x), o.n_mult ? `${o.n_mult} with EBITDA · ${fmtX(o.p25_ebitda_x)}–${fmtX(o.p75_ebitda_x)}` : null)}
        {tile('Median revenue multiple', fmtX(o.med_revenue_x))}
        {tile('Median price', money(o.med_ev), o.med_ebitda ? `on ${money(o.med_ebitda)} EBITDA` : null)}
        {tile('Fees at close', money(o.fees), o.ev_total ? `${(100 * o.fees / o.ev_total).toFixed(1)}% of ${money(o.ev_total)}` : null)}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4, alignItems: 'center', padding: '0 12px' }}>
          {[null, ...years.slice(-5)].map((y) => (
            <button key={y || 'all'} onClick={() => setYear(y)} style={{
              padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 999, fontSize: 11, cursor: 'pointer',
              background: year === y ? 'var(--text)' : 'transparent', color: year === y ? 'var(--surface)' : 'var(--text-3)',
            }}>{y || 'All'}</button>
          ))}
        </div>
      </div>
      {top.length > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '6px 14px 10px', flexWrap: 'wrap', fontSize: 11.5 }}>
          {top.map((r) => (
            <span key={r.key} title={`${r.n} closed · ${r.n_mult} with a multiple · ${fmtX(r.p25_ebitda_x)}–${fmtX(r.p75_ebitda_x)}`}
              style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              {r.key} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtX(r.med_ebitda_x)}</b> <span style={{ color: 'var(--text-4)' }}>×{r.n}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

Object.assign(window, { ClosingDrawer, ClosingSection, ClosedDealsStrip, isWonSlug });
